import { Box, TextField } from '@mui/material';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import React, { useCallback, useEffect, useState } from 'react';
import ProcedureDTO from '@src/adapters/dto/procedure/ProcedureDTO';
import { useFindProceduresLazy } from '@src/adapters/hooks/procedures/useFindProcedures';

interface Props {
  selectProcedure(procedure: ProcedureDTO): void;
  selectedProcedure?: ProcedureDTO;
  updatedProcedure?: ProcedureDTO;
}

function List({ selectedProcedure, updatedProcedure, selectProcedure }: Props) {
  const { execute: find } = useFindProceduresLazy();
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [recordCount, setRecordCount] = useState(0);
  const [procedures, setProceduress] = useState<ProcedureDTO[]>([]);
  const [filter, setFilter] = useState('');

  const findProcedures = useCallback(() => {
    find({
      filter,
      pageSize: rowsPerPage,
      currentPage: currentPage,
    }).then((result) => {
      if (result.procedures) {
        setProceduress(
          result.procedures.sort((a, b) => a.name.localeCompare(b.name))
        );
      }
      if (result.recordCount) {
        setRecordCount(result.recordCount);
      }
    });
  }, [find, rowsPerPage, currentPage, filter]);

  useEffect(() => {
    findProcedures();
  }, [findProcedures]);

  useEffect(() => {
    if (updatedProcedure) {
      setProceduress((prev) =>
        prev.find((c) => c.id === updatedProcedure.id)
          ? prev.map((c) =>
              c.id === updatedProcedure.id ? updatedProcedure : c
            )
          : [...prev, updatedProcedure]
      );
    }
  }, [updatedProcedure]);

  const handleRowClick = (
    event: React.MouseEvent<unknown>,
    procedure: ProcedureDTO
  ) => {
    selectProcedure(procedure);
  };

  const isSelected = (procedure: ProcedureDTO) => {
    return selectedProcedure && selectedProcedure.id === procedure.id;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={(theme) => ({ padding: theme.spacing(2) })}>
        <TextField
          label="Filter by name"
          variant="outlined"
          onChange={(e) => setFilter(e.target.value)}
        />
      </Box>
      <Box>
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size="small"
            aria-label="Contents table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Variant</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {procedures.map((procedure, index) => {
                const isItemSelected = isSelected(procedure);
                return (
                  <TableRow
                    hover
                    onClick={(event) => handleRowClick(event, procedure)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={index}
                    selected={isItemSelected}>
                    <TableCell scope="row">{procedure.name}</TableCell>
                    <TableCell scope="row">{procedure.variant}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={recordCount}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          onPageChange={(event, number) => setCurrentPage(number)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setCurrentPage(0);
          }}
        />
      </Box>
    </Box>
  );
}

export default List;
